import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getProjects } from '../../packages/api-client/pm-api';
import {
    deleteMonitoringRoute, listSentryProjects, saveMonitoringRoute, saveSentryDiscovery, sentryDiscoveryError,
    type MonitoringConnection, type MonitoringProjectRoute, type MonitoringRouteInput, type SentryProject, type SentryProjectPage,
} from '../../packages/api-client/monitoring-api';
import { Dialog } from '../../packages/ui';

export interface Destination { id: string; name: string }

export function useMonitoringProjects(scope: string[]) {
    return useInfiniteQuery({
        queryKey: ['monitoring-projects', ...scope], initialPageParam: 1,
        queryFn: ({ pageParam }) => getProjects(pageParam, 50),
        getNextPageParam: last => last.meta.page < last.meta.total_pages ? last.meta.page + 1 : undefined,
        retry: false,
    });
}

export function SentryProjectPicker({ load, destinations, selected, onChange, defaultDestination = '', single = false, excluded = [] }: {
    load: (query: string, cursor: string) => Promise<SentryProjectPage>; destinations: Destination[];
    selected: MonitoringRouteInput[]; onChange: (routes: MonitoringRouteInput[]) => void;
    defaultDestination?: string; single?: boolean; excluded?: string[];
}) {
    const [query, setQuery] = useState('');
    const [loadedQuery, setLoadedQuery] = useState('');
    const [projects, setProjects] = useState<SentryProject[]>([]);
    const [cursor, setCursor] = useState('');
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    async function fetchPage(more: boolean) {
        if (busy) return;
        setBusy(true); setError('');
        try {
            const result = await load(more ? loadedQuery : query.trim(), more ? cursor : '');
            setProjects(previous => Array.from(new Map([...(more ? previous : []), ...result.data].map(p => [p.id, p])).values()));
            setCursor(result.next_cursor || ''); setLoaded(true);
            if (!more) setLoadedQuery(query.trim());
        } catch (err) { setError(sentryDiscoveryError(err)); }
        finally { setBusy(false); }
    }
    const available = projects.filter(p => !excluded.includes(p.id));
    return <div className="monitoring-picker">
        <label className="monitoring-field">Search Sentry projects<input value={query} maxLength={100} onChange={e => setQuery(e.target.value)} placeholder="Project name or slug" /></label>
        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void fetchPage(false)}>{busy ? 'Loading projects…' : loaded ? 'Search / refresh projects' : 'Load Sentry projects'}</button>
        {error && <p role="alert">{error}</p>}
        {loaded && !available.length && <p className="monitoring-note">No additional projects found. Try another search or check the token&apos;s organization access.</p>}
        {!!available.length && <ul className="monitoring-project-options" aria-label="Sentry projects">
            {available.map(p => <li key={p.id}>
                <label><input type="checkbox" disabled={!single && selected.length >= 100 && !selected.some(r => r.external_project_id === p.id)} checked={selected.some(r => r.external_project_id === p.id)} onChange={e => {
                    onChange(e.target.checked
                        ? [...(single ? [] : selected), { external_project_id: p.id, external_project_name: `${p.name} (${p.slug})`, project_id: defaultDestination }]
                        : selected.filter(r => r.external_project_id !== p.id));
                }} /> <strong>{p.name}</strong> <span className="monitoring-note">{p.slug}</span></label>
            </li>)}
        </ul>}
        {cursor && <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void fetchPage(true)}>Load more Sentry projects</button>}
        {!!selected.length && <div className="monitoring-selected">
            <h3>Selected projects ({selected.length})</h3>
            {selected.map(route => <div key={route.external_project_id}>
                <label className="monitoring-field">Destination for {route.external_project_name || route.external_project_id}
                    <select required value={route.project_id} onChange={e => onChange(selected.map(r => r.external_project_id === route.external_project_id ? { ...r, project_id: e.target.value } : r))}>
                        <option value="">Choose a KaziAtlas project</option>
                        {destinations.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </label>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => onChange(selected.filter(r => r.external_project_id !== route.external_project_id))}>Remove selection<span className="sr-only">: {route.external_project_name || route.external_project_id}</span></button>
            </div>)}
            <p className="monitoring-note">Several Sentry projects can share the same destination. Only selected projects send issues to this workspace.</p>
        </div>}
    </div>;
}

function DiscoverySettings({ connection, onSaved }: { connection: MonitoringConnection; onSaved: () => Promise<void> }) {
    const [editing, setEditing] = useState(!connection.has_api_token);
    const [org, setOrg] = useState(connection.organization_slug || '');
    const [token, setToken] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    if (!editing) return <div className="monitoring-actions"><span>Sentry organization: {connection.organization_slug}</span><button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>Update API token</button></div>;
    return <form onSubmit={async e => {
        e.preventDefault(); if (busy) return; setBusy(true); setError('');
        try {
            await saveSentryDiscovery(connection.id, { organization_slug: org.trim(), api_token: token });
            setToken(''); await onSaved(); setEditing(false);
        } catch (err) { setError(`${sentryDiscoveryError(err)} Re-enter the token to retry.`); }
        finally { setToken(''); setBusy(false); }
    }}>
        <p className="monitoring-note">Enable project discovery with a Sentry API token. The token must belong to this integration and have Organization read permission. It is encrypted when saved and never displayed again.</p>
        <fieldset disabled={busy} className="monitoring-form">
            <legend className="sr-only">Sentry project discovery credentials</legend>
            <label className="monitoring-field">Organization slug<input value={org} onChange={e => setOrg(e.target.value)} required maxLength={100} placeholder="oneplus-global" /></label>
            <label className="monitoring-field">Sentry API token<input type="password" autoComplete="new-password" value={token} onChange={e => setToken(e.target.value)} required minLength={16} maxLength={512} /></label>
            {error && <p role="alert">{error}</p>}
            <div className="monitoring-actions"><button type="submit" className="btn btn-primary btn-sm">{busy ? 'Verifying…' : 'Save project discovery'}</button>
                {connection.has_api_token && <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setToken(''); setEditing(false); }}>Cancel</button>}</div>
        </fieldset>
    </form>;
}

function RouteEditor({ route, connectionID, destinations, refresh }: {
    route: MonitoringProjectRoute; connectionID: string; destinations: Destination[]; refresh: () => Promise<void>;
}) {
    const [destination, setDestination] = useState(route.project_id);
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    async function save(remove: boolean) {
        if (busy) return; setBusy(true); setError(''); setNotice('');
        try {
            if (remove) await deleteMonitoringRoute(connectionID, route.external_project_id);
            else await saveMonitoringRoute(connectionID, { external_project_id: route.external_project_id, external_project_name: route.external_project_name, project_id: destination });
            setConfirm(false); await refresh(); if (!remove) setNotice('Destination updated. Existing tasks stay in their original project.');
        } catch (err) { setError(sentryDiscoveryError(err)); }
        finally { setBusy(false); }
    }
    return <li>
        <strong>{route.external_project_name || `Sentry project ${route.external_project_id}`}</strong>
        <p className="monitoring-note">Source ID: {route.external_project_id} · {route.status === 'ready' ? 'Ready' : 'Destination unavailable'}</p>
        <label className="monitoring-field">KaziAtlas destination for {route.external_project_name || route.external_project_id}
            <select value={destination} disabled={busy} onChange={e => setDestination(e.target.value)}>
                {!destinations.some(p => p.id === route.project_id) && <option value={route.project_id}>{route.project_name} (unavailable)</option>}
                {destinations.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
        </label>
        <div className="monitoring-actions">
            <Link to={`/projects/${route.project_id}`}>Open destination</Link>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy || destination === route.project_id} onClick={() => void save(false)}>Save destination</button>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setConfirm(true)}>Remove mapping<span className="sr-only">: {route.external_project_id}</span></button>
        </div>
        {notice && <p role="status">{notice}</p>}{error && !confirm && <p role="alert">{error}</p>}
        <Dialog open={confirm} title="Remove project mapping?" onClose={() => { if (!busy) setConfirm(false); }} footer={<>
            <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setConfirm(false)}>Cancel</button>
            <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => void save(true)}>Remove mapping</button>
        </>}>
            <p>Stop receiving new issues for Sentry project {route.external_project_id}. Saved issues and tasks remain. Other project mappings keep working.</p>
            {error && <p role="alert">{error}</p>}
        </Dialog>
    </li>;
}

export function MonitoringProjectRoutes({ connection, scope, refresh }: { connection: MonitoringConnection; scope: string[]; refresh: () => Promise<void> }) {
    const projects = useMonitoringProjects(scope);
    const destinations = projects.data?.pages.flatMap(p => p.data).filter(p => p.status !== 'archived' && !p.deleted_at) ?? [];
    const [adding, setAdding] = useState(false);
    const [selected, setSelected] = useState<MonitoringRouteInput[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const routes: MonitoringProjectRoute[] = connection.routes ?? (connection.external_project_id && connection.project_id ? [{
        external_project_id: connection.external_project_id, project_id: connection.project_id,
        project_name: connection.project_name || '', status: connection.intake_status === 'unavailable' ? 'unavailable' : 'ready',
    }] : []);
    return <section className="card monitoring-card" aria-labelledby="monitoring-routes-title">
        <h2 id="monitoring-routes-title">Project mappings</h2>
        <p className="monitoring-note">One Sentry integration and webhook can receive issues from several projects. Choose a KaziAtlas destination for each. Changing a destination never moves existing tasks.</p>
        <DiscoverySettings connection={connection} onSaved={refresh} />
        {projects.isPending && <p>Loading KaziAtlas projects…</p>}
        {projects.isError && <p role="alert">KaziAtlas projects could not be loaded. <button type="button" onClick={() => void projects.refetch()}>Retry</button></p>}
        {projects.hasNextPage && <button type="button" className="btn btn-secondary btn-sm" disabled={projects.isFetchingNextPage} onClick={() => void projects.fetchNextPage()}>Load more KaziAtlas projects</button>}
        {routes.length ? <ul className="monitoring-issues">{routes.map(route => <RouteEditor key={`${route.external_project_id}:${route.project_id}`} route={route} connectionID={connection.id} destinations={destinations} refresh={refresh} />)}</ul>
            : <p className="monitoring-note">No project mappings. Add one to receive issues.</p>}
        {!adding && <button type="button" className="btn btn-secondary btn-sm" disabled={!connection.has_api_token || !destinations.length || routes.length >= 100} onClick={() => { setSelected([]); setError(''); setAdding(true); }}>Add Sentry project</button>}
        {adding && <div>
            <fieldset className="monitoring-form" disabled={busy}>
                <legend className="sr-only">Add Sentry project mapping</legend>
                <SentryProjectPicker single load={(query, cursor) => listSentryProjects(connection.id, query, cursor)} destinations={destinations} selected={selected} onChange={setSelected} excluded={routes.map(r => r.external_project_id)} />
                {error && <p role="alert">{error}</p>}
                <div className="monitoring-actions">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAdding(false)}>Cancel adding project</button>
                    <button type="button" className="btn btn-primary btn-sm" disabled={!selected[0]?.project_id} onClick={async () => {
                        if (busy || !selected[0]?.project_id) return; setBusy(true); setError('');
                        try { await saveMonitoringRoute(connection.id, selected[0]); await refresh(); setSelected([]); setAdding(false); }
                        catch (err) { setError(sentryDiscoveryError(err)); } finally { setBusy(false); }
                    }}>{busy ? 'Saving…' : 'Save mapping'}</button>
                </div>
            </fieldset>
        </div>}
    </section>;
}
